import React, { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { LuX, LuSend, LuBot, LuPhone } from 'react-icons/lu';
import { FaWhatsapp } from 'react-icons/fa';
import { services } from '../../data/services';

const PHONE_DISPLAY = '+252 61 1178748';
const PHONE_TEL = 'tel:+252611178748';
const WHATSAPP_URL = 'https://wa.me/252611178748';

const INITIAL_BOT_MESSAGE = "Hello! I'm the RaadsoTech assistant. How can I help you today?";

const GREETING_PATTERNS = [
  'salamu aleykum',
  'salam aleikum',
  'asalamu alaykum',
  'assalamu alaikum',
  'salaam',
  'salam',
  'asc',
  'wcs',
  'wa aleykum',
  'hello',
  'hi',
  'hey',
  'marhaba',
  'marhaban',
  'welcome',
  'good morning',
  'good afternoon',
  'good evening',
];

const SERVICE_QUICK_REPLIES = services.map((service) => ({
  id: service.id,
  label: service.title,
}));

const WELCOME_TEXT =
  "Wa aleykum assalam — welcome to RaadsoTech! 👋\n\nWhich service can I help you with today?";

const CONTACT_TEXT =
  `Great choice! To move quickly, contact us now:\n\n📞 ${PHONE_DISPLAY}\n\nPick WhatsApp or Call — we will get back to you fast.`;

function normalize(text = '') {
  return String(text)
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^\w\s+#./$-]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function isGreeting(query) {
  return GREETING_PATTERNS.some(
    (phrase) => query === phrase || query.startsWith(`${phrase} `) || query.includes(` ${phrase}`),
  );
}

function findService(query) {
  return services.find((service) => {
    const title = normalize(service.title);
    const id = normalize(service.id.replace(/-/g, ' '));
    return (
      query.includes(normalize(service.id))
      || query.includes(id)
      || query.includes(title)
      || title.split(' ').some((word) => word.length > 4 && query.includes(word))
    );
  });
}

function formatServicePrices(service) {
  const lines = (service.packages || []).map(
    (pkg) => `• ${pkg.name}: ${pkg.price}`,
  );
  return `Here are the packages for ${service.title}:\n\n${lines.join('\n')}\n\n${CONTACT_TEXT}`;
}

const ChatbotWindow = ({ isOpen, onClose }) => {
  const [messages, setMessages] = useState([
    { text: INITIAL_BOT_MESSAGE, sender: 'bot' },
  ]);
  const [input, setInput] = useState('');
  const [step, setStep] = useState('greeting'); // greeting | service | contact
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (!isOpen) return undefined;
    setMessages([{ text: INITIAL_BOT_MESSAGE, sender: 'bot' }]);
    setStep('greeting');
    setInput('');
    return undefined;
  }, [isOpen]);

  const pushBot = (payload, delay = 500) => {
    setTimeout(() => {
      setMessages((prev) => [...prev, { sender: 'bot', ...payload }]);
    }, delay);
  };

  const handleServiceChoice = (service) => {
    if (!service) return;
    setStep('contact');
    pushBot({
      text: formatServicePrices(service),
      contacts: true,
    });
  };

  const processUserText = (rawText) => {
    const text = rawText.trim();
    if (!text) return;

    setMessages((prev) => [...prev, { text, sender: 'user' }]);
    const query = normalize(text);

    if (isGreeting(query)) {
      setStep('service');
      const reply = query.includes('salam') || query.includes('asc') || query.includes('wcs')
        ? WELCOME_TEXT
        : "Welcome to RaadsoTech! 👋\n\nWhich service can I help you with today?";
      pushBot({
        text: reply,
        quickReplies: SERVICE_QUICK_REPLIES,
      });
      return;
    }

    if (query.includes('whatsapp') || query.includes('whats app')) {
      pushBot({
        text: `Open WhatsApp to chat with us on ${PHONE_DISPLAY}.`,
        contacts: true,
      });
      return;
    }

    if (query.includes('call') || query.includes('phone') || query.includes('number')) {
      pushBot({
        text: `You can call us now on ${PHONE_DISPLAY}.`,
        contacts: true,
      });
      return;
    }

    const matchedService = findService(query);
    if (matchedService) {
      handleServiceChoice(matchedService);
      return;
    }

    if (query.includes('service') || query.includes('price') || query.includes('help')) {
      setStep('service');
      pushBot({
        text: 'Sure — which service do you need help with?',
        quickReplies: SERVICE_QUICK_REPLIES,
      });
      return;
    }

    pushBot({
      text: 'Please choose a service below, or send a greeting to restart.',
      quickReplies: SERVICE_QUICK_REPLIES,
    });
  };

  const handleSend = (e) => {
    e.preventDefault();
    if (!input.trim()) return;
    const value = input;
    setInput('');
    processUserText(value);
  };

  const handleQuickReply = (reply) => {
    setMessages((prev) => [...prev, { text: reply.label, sender: 'user' }]);
    const service = services.find((item) => item.id === reply.id);
    handleServiceChoice(service);
  };

  if (!isOpen) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20, scale: 0.95 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: 20, scale: 0.95 }}
      className="fixed bottom-28 right-6 z-[100] flex w-80 flex-col overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-2xl dark:border-white/10 dark:bg-[#0f0f0f] md:right-10 md:w-96"
      style={{ height: '460px' }}
    >
      <div className="flex items-center justify-between bg-primary p-4 text-black">
        <div className="flex items-center gap-3">
          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-white">
            <LuBot size={20} className="text-primary" />
          </div>
          <div>
            <h3 className="text-sm font-semibold">RaadsoTech Bot</h3>
            <span className="flex items-center gap-1 text-xs opacity-80">
              <span className="inline-block h-2 w-2 rounded-full bg-green-500" /> Online
            </span>
          </div>
        </div>
        <button type="button" onClick={onClose} className="text-black/70 hover:text-black">
          <LuX size={20} />
        </button>
      </div>

      <div className="flex flex-1 flex-col gap-3 overflow-y-auto bg-gray-50/50 p-4 dark:bg-[#0b0b0b]">
        {messages.map((msg, idx) => (
          <div key={`${msg.sender}-${idx}`} className={`flex ${msg.sender === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div className="max-w-[88%]">
              <div
                className={`whitespace-pre-line rounded-2xl p-3 text-sm ${
                  msg.sender === 'user'
                    ? 'rounded-br-sm bg-black text-white'
                    : 'rounded-bl-sm border border-gray-100 bg-white text-gray-800 shadow-sm dark:border-white/10 dark:bg-[#121212] dark:text-gray-100'
                }`}
              >
                {msg.text}
              </div>

              {msg.quickReplies?.length ? (
                <div className="mt-2 flex flex-wrap gap-2">
                  {msg.quickReplies.map((reply) => (
                    <button
                      key={reply.id}
                      type="button"
                      onClick={() => handleQuickReply(reply)}
                      className="rounded-full border border-primary/40 bg-primary/10 px-3 py-1.5 text-left text-xs font-medium text-black transition hover:bg-primary dark:text-white"
                    >
                      {reply.label}
                    </button>
                  ))}
                </div>
              ) : null}

              {msg.contacts ? (
                <div className="mt-2 flex flex-wrap gap-2">
                  <a
                    href={WHATSAPP_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 rounded-full bg-[#25D366] px-3 py-2 text-xs font-semibold text-white transition hover:brightness-95"
                  >
                    <FaWhatsapp size={14} />
                    WhatsApp
                  </a>
                  <a
                    href={PHONE_TEL}
                    className="inline-flex items-center gap-1.5 rounded-full bg-black px-3 py-2 text-xs font-semibold text-white transition hover:bg-gray-800"
                  >
                    <LuPhone size={14} />
                    Call Me
                  </a>
                </div>
              ) : null}
            </div>
          </div>
        ))}
        <div ref={messagesEndRef} />
      </div>

      <form
        onSubmit={handleSend}
        className="flex items-center gap-2 border-t border-gray-100 bg-white p-3 dark:border-white/10 dark:bg-[#0f0f0f]"
      >
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Type your message..."
          className="flex-1 rounded-full border border-gray-200 bg-gray-50 px-4 py-2 text-sm text-black placeholder:text-gray-400 focus:border-primary focus:outline-none dark:border-white/10 dark:bg-[#151515] dark:text-white dark:placeholder:text-gray-500"
        />
        <button
          type="submit"
          className="flex h-10 w-10 items-center justify-center rounded-full bg-primary text-black transition-transform hover:scale-105"
        >
          <LuSend size={16} />
        </button>
      </form>
    </motion.div>
  );
};

export default ChatbotWindow;
